// Public read of the Allergen Matrix (items + categories) from Supabase.
//
// Replaces the Apps Script `?action=allergenCatalog&site=…` / `allergenCategories` feeds
// the FOH tablet used — same PUBLIC exposure (both were unauthenticated GETs), served from
// Supabase now. The (hidden) allergen tile renders these; writes stay admin-only (CC →
// admin-data).
//
// ─── THE SITE FILTER MOVES HERE ──────────────────────────────────────────────
// The old feed filtered by site SERVER-side (`&site=<key>` → getAllergenCatalogPublic
// only returned items whose `sites` array includes that key). Reading Supabase gives us
// every active row, so the filter moves client-side and must match exactly, or a site
// starts showing items it doesn't serve. Items are ~21 rows, so fetch-all-and-filter is
// cheap and faithful. No key normalisation: allergens are FOH-only (tc/arndale/meadowhall)
// — the kitchen never reads them, so the mcr→kitchen trap does not apply.
import { supabase } from "./supabaseClient.js";

// snake_case row -> the API item shape foh.jsx already renders (AllergenInfo +
// _allergenEffective). Exported for the unit test (allergens.test.mjs).
export function mapItem(r) {
  return {
    itemId: r.item_id,
    name: r.name,
    categoryId: r.category_id || "",
    veg: r.veg === true,
    vegan: r.vegan === true,
    imageUrl: r.image_url || "",
    allergens: r.allergens || {},           // jsonb -> object; _allergenEffective reads it
    siteOverrides: r.site_overrides || {},  // PER-FIELD merge happens client-side, unchanged
    sites: Array.isArray(r.sites) ? r.sites : [],
    status: r.status,
  };
}

export function mapCategory(r) {
  return { id: r.category_id, name: r.name || "", icon: r.icon || "", order: r.sort_order || 0, status: r.status };
}

// ── Empty read is a FAILURE here, NOT a valid answer — the OPPOSITE of staff certs. ──
// There are 21 live items and 4 categories; every FOH site serves ≥20. So an empty read
// means RLS denied it or the copy hasn't run — never "no allergens". If we let empty
// through, foh.jsx's mount effect setStates it AND writes it to localStorage
// (`allergenCatalogCache_<site>`), poisoning the cache; the hidden tile then renders blank
// the instant someone flips it live, and a reload won't heal it (the empty cache sticks).
// So throw — foh.jsx's `.catch` keeps the last good cache, exactly as the old
// `r.ok ? r.json() : null` path did on a backend hiccup. (This is tileOverrides.js's guard,
// which is right HERE because — like tiles — the true state is never empty.)
export async function fetchAllergenCatalog(siteKey) {
  const site = String(siteKey ?? "").trim().toLowerCase();
  const { data, error } = await supabase
    .from("allergen_items")
    .select("item_id,name,category_id,veg,vegan,image_url,allergens,site_overrides,sites,status")
    .eq("status", "active");
  if (error) throw new Error(error.message || "Failed to load allergen catalog");
  if (!data || data.length === 0) throw new Error("allergen_items returned no rows — refusing to overwrite the cache");
  return data.filter((r) => Array.isArray(r.sites) && r.sites.includes(site)).map(mapItem);
}

export async function fetchAllergenCategories() {
  const { data, error } = await supabase
    .from("allergen_categories")
    .select("category_id,name,icon,sort_order,status")
    .eq("status", "active")
    .order("sort_order", { ascending: true })
    .order("category_id", { ascending: true });   // tie-break: Postgres sort is unstable on equal sort_order
  if (error) throw new Error(error.message || "Failed to load allergen categories");
  if (!data || data.length === 0) throw new Error("allergen_categories returned no rows — refusing to overwrite the cache");
  return data.map(mapCategory);
}
